import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { showToast } from "../../../utils/customToast";
import { SECURITY_KEY } from "../../../globals";
import SearchableSelect from "../../../Component/SearchableSelect/SearchableSelect";
import "./addfares.css";

const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";
const BASE_REST_API_URL =
  process.env.REACT_APP_BASE_REST_API_URL || "https://etourrestapi.responseinfoway.com/restApi";

function Fares() {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isReceivable =
    location.state?.isReceivable ||
    searchParams.get("type") === "receivable" ||
    location.pathname.includes("receivable");

  const [packageName, setPackageName] = useState("");
  const [selectedPartyID, setSelectedPartyID] = useState("");
  const [partyNameInput, setPartyNameInput] = useState("");
  const [partiesList, setPartiesList] = useState([]);
  const [packageMasterList, setPackageMasterList] = useState([]);
  const [existingReceivablePackages, setExistingReceivablePackages] = useState([]);
  const [packageType, setPackageType] = useState(0); // 0 = Day Wise / Local, 1 = KM Wise / Outstation
  const [fareRows, setFareRows] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [selectedVehicleToAdd, setSelectedVehicleToAdd] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalPackageName, setOriginalPackageName] = useState("");
  const [selectedCompID, setSelectedCompID] = useState("");
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [saving, setSaving] = useState(false);

  // Payable & Receivable Custom Rates state
  const [vendorsList, setVendorsList] = useState([]);
  const [customRates, setCustomRates] = useState([]); // [{VendorID/PartyID, VendorName/PartyName, Vtype, vehicleTypeName, ...}]
  const [selectedVendorToAdd, setSelectedVendorToAdd] = useState("");
  const [selectedPartyToAdd, setSelectedPartyToAdd] = useState("");
  const [selectedVtypeForCustom, setSelectedVtypeForCustom] = useState("");
  const [isSavingCustom, setIsSavingCustom] = useState(false);

  useEffect(() => {
    const load = () => {
      const c = JSON.parse(localStorage.getItem("selectedCompany"));
      if (c?.id) { setSelectedCompID(c.id); setSelectedCompanyName(c.name); }
      else { setSelectedCompID(""); setSelectedCompanyName(""); }
    };
    load();
    window.addEventListener("companyChanged", load);
    window.addEventListener("storage", (e) => { if (e.key === "selectedCompany") load(); });
    return () => window.removeEventListener("companyChanged", load);
  }, []);

  useEffect(() => { fetchVehicleTypes(); }, []);

  useEffect(() => {
    if (selectedCompID) {
      fetchPackageMasterList(selectedCompID);
      if (isReceivable) {
        fetchParties(selectedCompID);
        fetchExistingReceivablePackages(selectedCompID);
      } else {
        fetchVendors(selectedCompID);
      }
    }
  }, [selectedCompID, isReceivable]);

  useEffect(() => {
    if (selectedCompID && location.state?.editPackageName) {
      setIsEditMode(true);
      setOriginalPackageName(location.state.editPackageName);
      setPackageName(location.state.editPackageName);
      setSelectedPartyID("0");
      fetchPackageFares(selectedCompID, location.state.editPackageName);
      // Load existing custom rates in edit mode
      if (isReceivable) {
        fetchReceivableCustomRates(selectedCompID, location.state.editPackageName);
      } else {
        fetchCustomRates(selectedCompID, location.state.editPackageName);
      }
    }
  }, [selectedCompID, location.state]);

  const fetchReceivableCustomRates = async (compId, pkgName) => {
    try {
      let j = null;
      try {
        const r = await fetch(`${BASE_API_URL}/fares/receivable-custom/${compId}/${encodeURIComponent(pkgName)}`);
        if (r.ok) j = await r.json();
      } catch (_) {}

      if ((!j || !j.success) && BASE_API_URL !== "http://localhost:5000/api") {
        try {
          const rLocal = await fetch(`http://localhost:5000/api/fares/receivable-custom/${compId}/${encodeURIComponent(pkgName)}`);
          if (rLocal.ok) j = await rLocal.json();
        } catch (_) {}
      }

      if (j && j.success && Array.isArray(j.data)) {
        setCustomRates(
          j.data.map((item) => ({
            ...item,
            PartyID: item.PartyID,
            PartyName: item.PartyName,
            vehicleTypeName:
              item.vehicleTypeName ||
              item.VehicleTypeName ||
              vehicleTypes.find((vt) => String(vt.ID) === String(item.Vtype))?.VTYPE ||
              (item.Vtype ? `Type ${item.Vtype}` : ""),
          }))
        );
      }
    } catch (err) {
      console.error("fetchReceivableCustomRates error:", err);
    }
  };

  const fetchCustomRates = async (compId, pkgName) => {
    try {
      let j = null;
      try {
        const r = await fetch(`${BASE_API_URL}/fares/payable-custom/${compId}/${encodeURIComponent(pkgName)}`);
        if (r.ok) j = await r.json();
      } catch (_) {}

      if ((!j || !j.success) && BASE_API_URL !== "http://localhost:5000/api") {
        try {
          const rLocal = await fetch(`http://localhost:5000/api/fares/payable-custom/${compId}/${encodeURIComponent(pkgName)}`);
          if (rLocal.ok) j = await rLocal.json();
        } catch (_) {}
      }

      if (j && j.success && Array.isArray(j.data)) {
        setCustomRates(
          j.data.map((item) => ({
            ...item,
            vehicleTypeName:
              item.vehicleTypeName ||
              item.VehicleTypeName ||
              vehicleTypes.find((vt) => String(vt.ID) === String(item.Vtype))?.VTYPE ||
              (item.Vtype ? `Type ${item.Vtype}` : ""),
          }))
        );
      }
    } catch (err) {
      console.error("fetchCustomRates error:", err);
    }
  };

  const fetchVendors = async (compId) => {
    if (!compId) return;
    try {
      // 1. Try Node backend API first
      try {
        const r = await fetch(`${BASE_API_URL}/fares/vendors/${compId}`);
        if (r.ok) {
          const j = await r.json();
          if (j.success && Array.isArray(j.data) && j.data.length > 0) {
            setVendorsList(j.data);
            return;
          }
        }
      } catch (_) {}

      // 2. Direct Fallback to ASP.NET REST API SelectData.aspx (GroupCODE = 23 for Sundry Creditors)
      const data = new URLSearchParams();
      data.append("SecurityKey", SECURITY_KEY);
      data.append("TableName", "HEAD_MAST");
      data.append("WhereCondition", `CompID=${compId} AND GroupCODE=23`);
      data.append("*", "*");

      const res = await fetch(`${BASE_REST_API_URL}/SelectData.aspx`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      const j = await res.json();
      if (Array.isArray(j.Response)) {
        const vendors = j.Response
          .filter((v) => v.HEADName && v.HEADName.trim() !== "")
          .map((v) => ({
            VendorID: v.HEAD_CODE,
            VendorName: v.HEADName.trim(),
          }))
          .sort((a, b) => a.VendorName.localeCompare(b.VendorName));
        setVendorsList(vendors);
      }
    } catch (err) {
      console.error("fetchVendors error:", err);
    }
  };

  // Fetch Sundry Creditor vendors for custom rates (payable only)
  useEffect(() => {
    if (!isReceivable && selectedCompID) {
      fetchVendors(selectedCompID);
    }
  }, [selectedCompID, isReceivable]);

  const fetchParties = async (compId) => {
    try {
      const r = await fetch(`${BASE_API_URL}/fares/parties/${compId}`);
      const j = await r.json();
      if (j.success) {
        setPartiesList(j.data || []);
        if (selectedPartyID && String(selectedPartyID) !== "0") {
          const match = (j.data || []).find((p) => String(p.PartyID) === String(selectedPartyID));
          if (match) setPartyNameInput(match.PartyName);
        } else if (String(selectedPartyID) === "0") {
          setPartyNameInput("All Parties (Common Fare)");
        }
      }
    } catch (err) { console.error(err); }
  };

  const fetchPackageMasterList = async (compId) => {
    try {
      const r = await fetch(`${BASE_API_URL}/fares/package-master/${compId}`);
      const j = await r.json();
      if (j.success) setPackageMasterList(j.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchExistingReceivablePackages = async (compId) => {
    try {
      const r = await fetch(`${BASE_API_URL}/fares/packages/${compId}?fareType=receivable`);
      const j = await r.json();
      if (j.success) setExistingReceivablePackages(j.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchVehicleTypes = async () => {
    try {
      const r = await fetch(`${BASE_API_URL}/fares/vehicle-types`);
      const j = await r.json();
      if (j.success) setVehicleTypes(j.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchPackageFares = async (compId, pkgName, partyId) => {
    try {
      let url = `${BASE_API_URL}/fares/package-fares/${compId}/${encodeURIComponent(pkgName)}`;
      const params = new URLSearchParams();
      if (isReceivable) params.append("fareType", "receivable");
      if (partyId) params.append("partyId", partyId);
      if (params.toString()) url += `?${params.toString()}`;

      const r = await fetch(url);
      const j = await r.json();
      if (j.success && j.data && j.data.length > 0) {
        const first = j.data[0];
        if (first.PackageType !== undefined && first.PackageType !== null) {
          setPackageType(Number(first.PackageType));
        }
        if (first.PartyID !== undefined && first.PartyID !== null) {
          setSelectedPartyID(String(first.PartyID));
        } else {
          setSelectedPartyID("0");
        }
        if (first.PartyName) {
          setPartyNameInput(first.PartyName);
        } else if (!first.PartyID || Number(first.PartyID) === 0) {
          setPartyNameInput("All Parties (Common Fare)");
        }
        const baselineFares = isReceivable
          ? j.data.filter((f) => !f.PartyID || Number(f.PartyID) === 0)
          : j.data.filter((f) => !f.HEAD_CODE || Number(f.HEAD_CODE) === 0);

        const seenVtypes = new Set();
        const uniqueFares = [];
        for (const f of baselineFares) {
          const vKey = String(f.Vtype);
          if (!seenVtypes.has(vKey)) {
            seenVtypes.add(vKey);
            uniqueFares.push(f);
          }
        }

        setFareRows(uniqueFares.map((f) => {
          const vt = vehicleTypes.find((v) => String(v.ID) === String(f.Vtype));
          return {
            VtypeId: f.Vtype,
            vehicleTypeName: f.VehicleTypeName || (vt ? vt.VTYPE : null) || `Type ${f.Vtype}`,
            BaseFare: f.BaseFare || 0, FuelRateKM: f.FuelRateKM || 0,
            LubRateKM: f.LubRateKM || 0, TimeRatePM: f.TimeRatePM || 0,
            IncludedKM: f.IncludedKM || 0, IncludedHrs: f.IncludedHrs || 0,
          };
        }));
      }
    } catch (err) { console.error(err); }
  };

  // In Create Receivable mode: show only packages whose receivable rates have NOT been created yet
  const uncreatedReceivablePackages = useMemo(() => {
    if (!isReceivable || isEditMode) return packageMasterList;
    const existingNames = new Set(
      (existingReceivablePackages || []).map((p) => (p.PackageName || "").trim().toLowerCase())
    );
    return packageMasterList.filter(
      (pm) => !existingNames.has((pm.PackageName || "").trim().toLowerCase())
    );
  }, [isReceivable, isEditMode, packageMasterList, existingReceivablePackages]);

  // For custom overrides: show only parties who do NOT already have custom rates in this package
  const availablePartiesToAdd = useMemo(() => {
    const existingPartyIds = new Set(customRates.map((r) => String(r.PartyID)));
    return partiesList.filter((p) => !existingPartyIds.has(String(p.PartyID)));
  }, [partiesList, customRates]);

  // For custom overrides: show only vendors who do NOT already have custom rates in this package
  const availableVendorsToAdd = useMemo(() => {
    const existingVendorIds = new Set(
      customRates.map((r) => String(r.VendorID || r.HEAD_CODE))
    );
    return vendorsList.filter((v) => !existingVendorIds.has(String(v.VendorID)));
  }, [vendorsList, customRates]);

  // Resolve vehicle names once vehicle types load
  useEffect(() => {
    if (vehicleTypes.length > 0) {
      if (fareRows.length > 0) {
        setFareRows((prev) =>
          prev.map((row) => {
            const vt = vehicleTypes.find((v) => String(v.ID) === String(row.VtypeId || row.Vtype));
            return {
              ...row,
              vehicleTypeName: (vt ? vt.VTYPE : null) || row.vehicleTypeName || row.VehicleTypeName || (row.VtypeId ? `Type ${row.VtypeId}` : ""),
            };
          })
        );
      }
      if (customRates.length > 0) {
        setCustomRates((prev) =>
          prev.map((row) => {
            const vt = vehicleTypes.find((v) => String(v.ID) === String(row.Vtype || row.VtypeId));
            return {
              ...row,
              vehicleTypeName: (vt ? vt.VTYPE : null) || row.vehicleTypeName || row.VehicleTypeName || (row.Vtype ? `Type ${row.Vtype}` : ""),
            };
          })
        );
      }
    }
  }, [vehicleTypes]);

  const addVehicleRow = () => {
    if (!selectedVehicleToAdd) return;
    const isDup = fareRows.some((r) => String(r.VtypeId) === String(selectedVehicleToAdd));
    if (isDup) {
      showToast.warn("This vehicle category is already added!");
      return;
    }
    const vt = vehicleTypes.find((v) => String(v.ID) === String(selectedVehicleToAdd));
    if (!vt) return;
    setFareRows((prev) => [
      ...prev,
      { VtypeId: vt.ID, vehicleTypeName: vt.VTYPE, BaseFare: 0, FuelRateKM: 0, LubRateKM: 0, TimeRatePM: 0, IncludedKM: 0, IncludedHrs: 0 },
    ]);
    setSelectedVehicleToAdd("");
  };

  const removeRow = (i) => setFareRows((prev) => prev.filter((_, idx) => idx !== i));

  const handleRowChange = (i, field, val) => {
    setFareRows((prev) => {
      const rows = [...prev];
      rows[i] = { ...rows[i], [field]: val };
      return rows;
    });
  };

  const addCustomRateRow = () => {
    if (isReceivable) {
      if (!selectedPartyToAdd || !selectedVtypeForCustom) return;
      const party = partiesList.find((p) => String(p.PartyID) === String(selectedPartyToAdd));
      const vt = vehicleTypes.find((v) => String(v.ID) === String(selectedVtypeForCustom));
      if (!party || !vt) return;

      const isDup = customRates.some(
        (r) => String(r.PartyID) === String(selectedPartyToAdd) && String(r.Vtype) === String(selectedVtypeForCustom)
      );
      if (isDup) {
        showToast.warn("This party and vehicle combination is already added!");
        return;
      }

      const baseline = fareRows.find((r) => String(r.VtypeId) === String(vt.ID)) || {};

      setCustomRates((prev) => [
        ...prev,
        {
          PartyID: party.PartyID,
          PartyName: party.PartyName,
          Vtype: vt.ID,
          vehicleTypeName: vt.VTYPE,
          BaseFare: baseline.BaseFare || 0,
          FuelRateKM: baseline.FuelRateKM || 0,
          LubRateKM: baseline.LubRateKM || 0,
          TimeRatePM: baseline.TimeRatePM || 0,
          IncludedKM: baseline.IncludedKM || 0,
          IncludedHrs: baseline.IncludedHrs || 0,
        },
      ]);
      setSelectedPartyToAdd("");
      setSelectedVtypeForCustom("");
      return;
    }

    if (!selectedVendorToAdd || !selectedVtypeForCustom) return;
    const vendor = vendorsList.find((v) => String(v.VendorID) === String(selectedVendorToAdd));
    const vt = vehicleTypes.find((v) => String(v.ID) === String(selectedVtypeForCustom));
    if (!vendor || !vt) return;

    const isDup = customRates.some(
      (r) => String(r.VendorID) === String(selectedVendorToAdd) && String(r.Vtype) === String(selectedVtypeForCustom)
    );
    if (isDup) {
      showToast.warn("This vendor and vehicle combination is already added!");
      return;
    }

    // Pre-populate with baseline rates configured for this vehicle category if present
    const baseline = fareRows.find((r) => String(r.VtypeId) === String(vt.ID)) || {};

    setCustomRates((prev) => [
      ...prev,
      {
        VendorID: vendor.VendorID,
        VendorName: vendor.VendorName,
        Vtype: vt.ID,
        vehicleTypeName: vt.VTYPE,
        BaseFare: baseline.BaseFare || 0,
        FuelRateKM: baseline.FuelRateKM || 0,
        LubRateKM: baseline.LubRateKM || 0,
        TimeRatePM: baseline.TimeRatePM || 0,
        IncludedKM: baseline.IncludedKM || 0,
        IncludedHrs: baseline.IncludedHrs || 0,
      },
    ]);
    setSelectedVendorToAdd("");
    setSelectedVtypeForCustom("");
  };

  const removeCustomRateRow = (i) => {
    setCustomRates((prev) => prev.filter((_, idx) => idx !== i));
  };

  const handleCustomRateChange = (i, field, val) => {
    setCustomRates((prev) => {
      const rows = [...prev];
      rows[i] = { ...rows[i], [field]: val };
      return rows;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!selectedCompID) { showToast.error("Company not selected!"); return; }
    if (!packageName.trim()) { showToast.error("Package Name is required!"); return; }
    if (fareRows.length === 0) { showToast.error("Add at least one vehicle type!"); return; }

    setSaving(true);
    try {
      const cleanedFares = fareRows.map((row) => {
        if (packageType === 1) {
          return {
            ...row,
            BaseFare: 0,
            IncludedHrs: 0,
            IncludedKM: Number(row.IncludedKM) || 0,
            FuelRateKM: Number(row.FuelRateKM) || 0,
            LubRateKM: Number(row.LubRateKM) || 0,
            TimeRatePM: 0,
          };
        }
        return {
          ...row,
          BaseFare: Number(row.BaseFare) || 0,
          IncludedHrs: Number(row.IncludedHrs) || 0,
          IncludedKM: Number(row.IncludedKM) || 0,
          FuelRateKM: Number(row.FuelRateKM) || 0,
          LubRateKM: Number(row.LubRateKM) || 0,
          TimeRatePM: Number(row.TimeRatePM) || 0,
        };
      });

      const payload = {
        CompID: selectedCompID,
        PackageName: packageName,
        OriginalPackageName: originalPackageName,
        PackageType: packageType,
        fares: cleanedFares,
        fareType: isReceivable ? "receivable" : "payable",
      };
      if (isReceivable) {
        payload.PartyID = 0; // Baseline rates are common (All Parties)
      }

      const r = await fetch(`${BASE_API_URL}/fares/save-package`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (j.success) {
        // Save party custom rates if receivable mode
        if (isReceivable && customRates.length >= 0) {
          const customPayload = {
            CompID: selectedCompID,
            packageName: packageName.trim(),
            customFares: customRates.map((r) => {
              if (packageType === 1) {
                return {
                  PartyID: r.PartyID,
                  Vtype: r.Vtype,
                  BaseFare: 0,
                  IncludedHrs: 0,
                  IncludedKM: Number(r.IncludedKM) || 0,
                  FuelRateKM: Number(r.FuelRateKM) || 0,
                  LubRateKM: Number(r.LubRateKM) || 0,
                  TimeRatePM: 0,
                };
              }
              return {
                PartyID: r.PartyID,
                Vtype: r.Vtype,
                BaseFare: Number(r.BaseFare) || 0,
                IncludedHrs: Number(r.IncludedHrs) || 0,
                IncludedKM: Number(r.IncludedKM) || 0,
                FuelRateKM: Number(r.FuelRateKM) || 0,
                LubRateKM: Number(r.LubRateKM) || 0,
                TimeRatePM: Number(r.TimeRatePM) || 0,
              };
            }),
          };

          try {
            let saved = false;
            try {
              const res1 = await fetch(`${BASE_API_URL}/fares/receivable-custom/save`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(customPayload),
              });
              if (res1.ok) {
                const j1 = await res1.json();
                if (j1.success) saved = true;
              }
            } catch (_) {}

            if (!saved && BASE_API_URL !== "http://localhost:5000/api") {
              await fetch("http://localhost:5000/api/fares/receivable-custom/save", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(customPayload),
              });
            }
          } catch (customErr) {
            console.error("Failed to save receivable custom rates:", customErr);
          }
        }

        // Save vendor custom rates if payable mode
        if (!isReceivable && customRates.length >= 0) {
          const customPayload = {
            CompID: selectedCompID,
            packageName: packageName.trim(),
            customFares: customRates.map((r) => {
              if (packageType === 1) {
                return {
                  VendorID: r.VendorID,
                  Vtype: r.Vtype,
                  BaseFare: 0,
                  IncludedHrs: 0,
                  IncludedKM: Number(r.IncludedKM) || 0,
                  FuelRateKM: Number(r.FuelRateKM) || 0,
                  LubRateKM: Number(r.LubRateKM) || 0,
                  TimeRatePM: 0,
                };
              }
              return {
                VendorID: r.VendorID,
                Vtype: r.Vtype,
                BaseFare: Number(r.BaseFare) || 0,
                IncludedHrs: Number(r.IncludedHrs) || 0,
                IncludedKM: Number(r.IncludedKM) || 0,
                FuelRateKM: Number(r.FuelRateKM) || 0,
                LubRateKM: Number(r.LubRateKM) || 0,
                TimeRatePM: Number(r.TimeRatePM) || 0,
              };
            }),
          };

          try {
            let saved = false;
            try {
              const res1 = await fetch(`${BASE_API_URL}/fares/payable-custom/save`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(customPayload),
              });
              if (res1.ok) {
                const j1 = await res1.json();
                if (j1.success) saved = true;
              }
            } catch (_) {}

            if (!saved && BASE_API_URL !== "http://localhost:5000/api") {
              await fetch("http://localhost:5000/api/fares/payable-custom/save", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(customPayload),
              });
            }
          } catch (customErr) {
            console.error("Failed to save custom rates:", customErr);
          }
        }

        showToast.save(`${isReceivable ? "Receivable package" : "Package"} saved for ${selectedCompanyName}`);
        const targetUrl = isReceivable ? "/showfares?type=receivable" : "/showfares";
        setTimeout(() => navigate(targetUrl, { state: { isReceivable } }), 1200);
      } else {
        showToast.error(j.message || "Failed to save.");
      }
    } catch (err) {
      showToast.error("Failed to save package.");
    } finally {
      setSaving(false);
    }
  };

  const available = useMemo(
    () => vehicleTypes.filter((vt) => !fareRows.some((r) => String(r.VtypeId) === String(vt.ID))),
    [vehicleTypes, fareRows]
  );

  const baseFields = useMemo(() => {
    if (packageType === 1) {
      return [
        { key: "IncludedKM", label: "Incl. KM" },
      ];
    }
    return [
      { key: "BaseFare", label: "Base Fare (₹)" },
      { key: "IncludedHrs", label: "Incl. Hrs" },
      { key: "IncludedKM", label: "Incl. KM" },
    ];
  }, [packageType]);

  const extraFields = useMemo(() => {
    if (packageType === 1) {
      return [
        { key: "FuelRateKM", label: "Fuel/KM" },
        { key: "LubRateKM", label: "Lub/KM" },
      ];
    }
    return [
      { key: "FuelRateKM", label: "Fuel/KM" },
      { key: "LubRateKM", label: "Lub/KM" },
      { key: "TimeRatePM", label: "Time/Hr" },
    ];
  }, [packageType]);

  const cancelUrl = isReceivable ? "/showfares?type=receivable" : "/showfares";

  return (
    <div className="dashboard-wrapper af-page-container">
      {/* 1. Header & Breadcrumbs matching Dashboard */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="dashboard-breadcrumb mb-0">
          <span>Master &gt; Packages &amp; Fares &gt;</span>
          <span className="text-dark fw-bold">
            {isEditMode
              ? isReceivable
                ? "Edit Receivable Package"
                : "Edit Payable Package"
              : isReceivable
              ? "Add Receivable Package"
              : "Add Payable Package"}
          </span>
        </div>
      </div>

      {/* 2. Main Form Card */}
      <div className="dash-card af-main-card">
        <div className="dash-card-header af-card-header">
          <div>
            <h5 className="af-header-title">
              {isEditMode
                ? isReceivable
                  ? "Edit Receivable Package Rates"
                  : "Edit Payable Package Rates"
                : isReceivable
                ? "Create New Receivable Package"
                : "Create New Payable Package"}
            </h5>
            <p className="af-subtitle">
              {isEditMode
                ? "Modify vehicle rates and included allowances"
                : `Configure ${isReceivable ? "receivable" : "payable"} rates for each vehicle category`}
            </p>
          </div>
          <button
            type="button"
            className="filter-btn-reset"
            onClick={() => navigate(cancelUrl, { state: { isReceivable } })}
          >
            <i className="fa-solid fa-arrow-left me-1"></i> Back to List
          </button>
        </div>

        <form onSubmit={handleSubmit} className="af-form">
          {/* Top Form Controls Row */}
          <div className="filter-bar-card af-top-controls-card">
            <div className="af-field-row af-single-row">
              <div className="af-field af-field-compact" style={{ minWidth: "260px" }}>
                <label>
                  Package Name <span style={{ color: "red" }}>*</span>
                </label>
                {isReceivable && !isEditMode ? (
                  <div style={{ width: "100%" }}>
                    <SearchableSelect
                      options={uncreatedReceivablePackages.map((pm) => ({
                        value: pm.PackageName,
                        label: `${pm.PackageName} (${Number(pm.PackageType) === 1 ? "Outstation" : "Local"})`,
                      }))}
                      value={packageName}
                      onChange={async (opt) => {
                        const val = opt ? (typeof opt === "object" ? opt.value : opt) : "";
                        setPackageName(val);
                        const found = packageMasterList.find(
                          (pm) => (pm.PackageName || "").toLowerCase() === String(val).trim().toLowerCase()
                        );
                        if (found && found.PackageType !== undefined) {
                          setPackageType(Number(found.PackageType));
                        }
                        if (val && selectedCompID) {
                          try {
                            const rPayable = await fetch(
                              `${BASE_API_URL}/fares/package-fares/${selectedCompID}/${encodeURIComponent(val)}?fareType=payable`
                            );
                            const jPayable = await rPayable.json();
                            if (jPayable.success && Array.isArray(jPayable.data) && jPayable.data.length > 0) {
                              const baseline = jPayable.data.filter(
                                (f) => !f.HEAD_CODE || Number(f.HEAD_CODE) === 0
                              );
                              const seen = new Set();
                              const unique = [];
                              baseline.forEach((f) => {
                                const k = String(f.Vtype);
                                if (!seen.has(k)) {
                                  seen.add(k);
                                  unique.push(f);
                                }
                              });
                              if (unique.length > 0) {
                                setFareRows(
                                  unique.map((f) => {
                                    const vt = vehicleTypes.find((v) => String(v.ID) === String(f.Vtype));
                                    return {
                                      VtypeId: f.Vtype,
                                      vehicleTypeName:
                                        f.VehicleTypeName || (vt ? vt.VTYPE : null) || `Type ${f.Vtype}`,
                                      BaseFare: f.BaseFare || 0,
                                      FuelRateKM: f.FuelRateKM || 0,
                                      LubRateKM: f.LubRateKM || 0,
                                      TimeRatePM: f.TimeRatePM || 0,
                                      IncludedKM: f.IncludedKM || 0,
                                      IncludedHrs: f.IncludedHrs || 0,
                                    };
                                  })
                                );
                              }
                            }
                          } catch (_) {}
                        }
                      }}
                      placeholder={
                        uncreatedReceivablePackages.length === 0
                          ? "-- No Uncreated Packages --"
                          : "-- Select Package --"
                      }
                      height="28px"
                      disabled={uncreatedReceivablePackages.length === 0}
                    />
                    {uncreatedReceivablePackages.length === 0 && (
                      <div
                        className="alert alert-warning mt-1 mb-0 py-1 px-2 d-flex align-items-center justify-content-between"
                        style={{ fontSize: "10.5px", borderRadius: "4px" }}
                      >
                        <span>
                          <i className="fa-solid fa-triangle-exclamation me-1 text-warning"></i>
                          All packages created. <strong>Make new package in Payable first.</strong>
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-dark py-0 px-2 ms-2 text-nowrap"
                          style={{ fontSize: "10px" }}
                          onClick={() => navigate("/fares")}
                        >
                          <i className="fa-solid fa-plus me-1"></i> Make in Payable
                        </button>
                      </div>
                    )}
                  </div>
                ) : isEditMode ? (
                  <input
                    type="text"
                    className="filter-input af-input-control"
                    value={packageName}
                    readOnly
                    disabled
                    style={{ backgroundColor: "#f1f5f9", cursor: "not-allowed" }}
                  />
                ) : (
                  <>
                    <input
                      type="text"
                      className="filter-input af-input-control"
                      list="availablePackagesList"
                      value={packageName}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPackageName(val);
                        const found = packageMasterList.find(
                          (pm) => (pm.PackageName || "").toLowerCase() === String(val).trim().toLowerCase()
                        );
                        if (found && found.PackageType !== undefined) {
                          setPackageType(Number(found.PackageType));
                        }
                      }}
                      placeholder="e.g. LOCAL DISPOSAL 8HR 80KM"
                      maxLength={50}
                      required
                    />
                    <datalist id="availablePackagesList">
                      {packageMasterList.map((pm) => (
                        <option key={pm.Id || pm.PackageName} value={pm.PackageName} />
                      ))}
                    </datalist>
                  </>
                )}
              </div>

              <div className="af-field af-type-field">
                <label>Package Type</label>
                <div className="chart-tabs-wrapper sf-type-tabs">
                  <button
                    type="button"
                    className={`chart-tab-btn ${packageType === 0 ? "active sf-tab-local" : ""}`}
                    onClick={() => setPackageType(0)}
                  >
                    <i className="fa-solid fa-sun me-1"></i> Local
                  </button>
                  <button
                    type="button"
                    className={`chart-tab-btn ${packageType === 1 ? "active sf-tab-outstation" : ""}`}
                    onClick={() => setPackageType(1)}
                  >
                    <i className="fa-solid fa-road me-1"></i> Outstation
                  </button>
                </div>
              </div>

              <div className="af-field af-vehicle-add-field">
                <label>Add Vehicle Category</label>
                <div className="d-flex align-items-center gap-1">
                  <div style={{ minWidth: "160px" }}>
                    <SearchableSelect
                      options={available.map((vt) => ({ value: vt.ID, label: vt.VTYPE }))}
                      value={selectedVehicleToAdd}
                      onChange={(opt) => setSelectedVehicleToAdd(opt ? (typeof opt === "object" ? opt.value : opt) : "")}
                      placeholder="Select vehicle…"
                      height="28px"
                    />
                  </div>
                  <button
                    type="button"
                    className="filter-btn-apply sf-primary-btn"
                    onClick={addVehicleRow}
                    disabled={!selectedVehicleToAdd}
                  >
                    <i className="fa-solid fa-plus me-1"></i> Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Fares Rates Table */}
          <div className="af-section">
            {fareRows.length === 0 ? (
              <div className="sf-empty af-empty-box">
                <i className="fa-solid fa-car"></i>
                <span>No vehicle rates added yet.</span>
                <span className="text-muted" style={{ fontSize: "10.5px" }}>
                  Select a vehicle above and click &quot;+ Add&quot; to configure its rates.
                </span>
              </div>
            ) : (
              <div className="sf-table-container af-table-wrap">
                <table className="dash-pixel-table af-table">
                  <colgroup>
                    {packageType === 1 ? (
                      <>
                        <col style={{ width: "32%" }} />
                        <col style={{ width: "22%" }} />
                        <col style={{ width: "21%" }} />
                        <col style={{ width: "21%" }} />
                        <col style={{ width: "4%" }} />
                      </>
                    ) : (
                      <>
                        <col style={{ width: "22%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "4%" }} />
                      </>
                    )}
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="sf-th-vtype sf-col-left" rowSpan="2">
                        Vehicle Type
                      </th>
                      <th colSpan={baseFields.length} className="sf-group-header sf-group-base">
                        INCLUDED PACKAGE
                      </th>
                      <th colSpan={extraFields.length} className="sf-group-header sf-group-extra">
                        ADDITIONAL RATES (EXTRA)
                      </th>
                      <th className="af-th-action text-center" rowSpan="2"></th>
                    </tr>
                    <tr>
                      {baseFields.map((f, idx) => (
                        <th
                          key={f.key}
                          className={`sf-th-base ${
                            idx === 0 ? "sf-th-base-first" : idx === baseFields.length - 1 ? "sf-th-base-last" : ""
                          } sf-col-center`}
                        >
                          {f.label}
                        </th>
                      ))}
                      {extraFields.map((f, idx) => (
                        <th
                          key={f.key}
                          className={`sf-th-extra ${
                            idx === 0 ? "sf-th-extra-first" : idx === extraFields.length - 1 ? "sf-th-extra-last" : ""
                          } sf-col-center`}
                        >
                          {f.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fareRows.map((row, i) => (
                      <tr key={i}>
                        <td className="sf-td-vtype sf-col-left">
                          <span>
                            {row.vehicleTypeName ||
                              row.VehicleTypeName ||
                              vehicleTypes.find((v) => String(v.ID) === String(row.VtypeId || row.Vtype))?.VTYPE ||
                              (row.VtypeId ? `Type ${row.VtypeId}` : "—")}
                          </span>
                        </td>
                        {baseFields.map((f, idx) => (
                          <td
                            key={f.key}
                            className={`sf-td-base ${
                              idx === 0 ? "sf-td-base-first" : idx === baseFields.length - 1 ? "sf-td-base-last" : ""
                            } sf-col-center`}
                          >
                            <input
                              type="number"
                              step="any"
                              className="af-number-input"
                              value={row[f.key]}
                              onChange={(e) => handleRowChange(i, f.key, e.target.value)}
                              required
                            />
                          </td>
                        ))}
                        {extraFields.map((f, idx) => (
                          <td
                            key={f.key}
                            className={`sf-td-extra ${
                              idx === 0 ? "sf-td-extra-first" : idx === extraFields.length - 1 ? "sf-td-extra-last" : ""
                            } sf-col-center`}
                          >
                            <input
                              type="number"
                              step="any"
                              className="af-number-input"
                              value={row[f.key]}
                              onChange={(e) => handleRowChange(i, f.key, e.target.value)}
                              required
                            />
                          </td>
                        ))}
                        <td className="text-center">
                          <button
                            type="button"
                            className="ledger_btn_delete"
                            onClick={() => removeRow(i)}
                            title="Remove vehicle rate"
                          >
                            <i className="fa-solid fa-trash-can"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Party/Vendor Specific Custom Rates (Optional Overrides) */}
          <div className="af-section" style={{ marginTop: "16px" }}>
            <div
              className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2"
              style={{ padding: "0 2px" }}
            >
              <div>
                <h6 className="af-header-title d-flex align-items-center gap-2">
                  <i className={`fa-solid ${isReceivable ? "fa-user-group" : "fa-user-tag"}`} style={{ color: "#008b8b" }}></i>
                  <span>{isReceivable ? "Party-Specific Custom Rates" : "Vendor-Specific Custom Rates"}</span>
                  <span
                    className="pill-badge"
                    style={{
                      background: "#f0fdfa",
                      color: "#0f766e",
                      border: "1px solid #ccfbf1",
                      fontSize: "9.5px",
                      fontWeight: "600",
                      padding: "1px 6px",
                    }}
                  >
                    Optional Overrides
                  </span>
                </h6>
                <p className="af-subtitle">
                  {isReceivable
                    ? "Set specific receivable rate overrides for individual Sundry Debtor parties on this package."
                    : "Set specific payable amount overrides for individual Sundry Creditor vendors on this package."}
                </p>
              </div>
            </div>

            {/* Add Custom Rate Toolbar Card */}
            <div className="filter-bar-card af-top-controls-card mb-2" style={{ margin: "0 0 10px 0" }}>
              <div className="af-field-row af-single-row">
                <div className="af-field af-field-compact">
                  <label>{isReceivable ? "Party (Sundry Debtor)" : "Vendor (Sundry Creditor)"}</label>
                  {isReceivable ? (
                    <SearchableSelect
                      options={availablePartiesToAdd.map((p) => ({ value: p.PartyID, label: p.PartyName }))}
                      value={selectedPartyToAdd}
                      onChange={(opt) => setSelectedPartyToAdd(opt ? (typeof opt === "object" ? opt.value : opt) : "")}
                      placeholder={
                        availablePartiesToAdd.length === 0
                          ? "-- All Parties Configured --"
                          : "-- Select Party --"
                      }
                      height="28px"
                      disabled={availablePartiesToAdd.length === 0}
                    />
                  ) : (
                    <SearchableSelect
                      options={availableVendorsToAdd.map((v) => ({ value: v.VendorID, label: v.VendorName }))}
                      value={selectedVendorToAdd}
                      onChange={(opt) => setSelectedVendorToAdd(opt ? (typeof opt === "object" ? opt.value : opt) : "")}
                      placeholder={
                        availableVendorsToAdd.length === 0
                          ? "-- All Vendors Configured --"
                          : "-- Select Vendor --"
                      }
                      height="28px"
                      disabled={availableVendorsToAdd.length === 0}
                    />
                  )}
                </div>

                <div className="af-field af-field-compact">
                  <label>Vehicle Type</label>
                  <SearchableSelect
                    options={
                      fareRows.length > 0
                        ? fareRows.map((r) => ({
                            value: r.VtypeId || r.Vtype,
                            label: r.vehicleTypeName || `Type ${r.VtypeId || r.Vtype}`,
                          }))
                        : vehicleTypes.map((vt) => ({ value: vt.ID, label: vt.VTYPE }))
                    }
                    value={selectedVtypeForCustom}
                    onChange={(opt) => setSelectedVtypeForCustom(opt ? (typeof opt === "object" ? opt.value : opt) : "")}
                    placeholder="-- Select Vehicle Category --"
                    height="28px"
                  />
                </div>

                <div className="af-field" style={{ flex: "0 0 auto" }}>
                  <button
                    type="button"
                    className="filter-btn-apply sf-primary-btn"
                    onClick={addCustomRateRow}
                    disabled={
                      isReceivable
                        ? !selectedPartyToAdd || !selectedVtypeForCustom
                        : !selectedVendorToAdd || !selectedVtypeForCustom
                    }
                  >
                    <i className="fa-solid fa-plus me-1"></i> Add Custom Rate
                  </button>
                </div>
              </div>
            </div>

            {/* Custom Rates Table */}
            {customRates.length === 0 ? (
              <div className="sf-empty af-empty-box" style={{ padding: "20px 16px" }}>
                <i className="fa-solid fa-users-gear" style={{ color: "#94a3b8" }}></i>
                <span>
                  {isReceivable
                    ? "No custom party rates added for this package."
                    : "No custom vendor rates added for this package."}
                </span>
                <span className="text-muted" style={{ fontSize: "10.5px" }}>
                  {isReceivable
                    ? "All parties will automatically use the baseline package rates above unless a specific override is added here."
                    : "All vendors will automatically use the baseline package rates above unless a specific override is added here."}
                </span>
              </div>
            ) : (
              <div className="sf-table-container af-table-wrap">
                <table className="dash-pixel-table af-table">
                  <colgroup>
                    {packageType === 1 ? (
                      <>
                        <col style={{ width: "26%" }} />
                        <col style={{ width: "20%" }} />
                        <col style={{ width: "17%" }} />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "5%" }} />
                      </>
                    ) : (
                      <>
                        <col style={{ width: "20%" }} />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "4%" }} />
                      </>
                    )}
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="sf-th-vtype sf-col-left" rowSpan="2">
                        {isReceivable ? "Party (Sundry Debtor)" : "Vendor (Sundry Creditor)"}
                      </th>
                        <th className="sf-col-center" rowSpan="2">
                          Vehicle Type
                        </th>
                        <th colSpan={baseFields.length} className="sf-group-header sf-group-base">
                          INCLUDED PACKAGE
                        </th>
                        <th colSpan={extraFields.length} className="sf-group-header sf-group-extra">
                          ADDITIONAL RATES (EXTRA)
                        </th>
                        <th className="af-th-action text-center" rowSpan="2"></th>
                      </tr>
                      <tr>
                        {baseFields.map((f, idx) => (
                          <th
                            key={f.key}
                            className={`sf-th-base ${
                              idx === 0 ? "sf-th-base-first" : idx === baseFields.length - 1 ? "sf-th-base-last" : ""
                            } sf-col-center`}
                          >
                            {f.label}
                          </th>
                        ))}
                        {extraFields.map((f, idx) => (
                          <th
                            key={f.key}
                            className={`sf-th-extra ${
                              idx === 0 ? "sf-th-extra-first" : idx === extraFields.length - 1 ? "sf-th-extra-last" : ""
                            } sf-col-center`}
                          >
                            {f.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {customRates.map((row, i) => (
                        <tr key={i}>
                          <td className="sf-td-vtype sf-col-left">
                            <i
                              className={`fa-solid ${isReceivable ? "fa-building" : "fa-user-tie"} me-1 text-teal`}
                              style={{ fontSize: "10px" }}
                            ></i>
                            <span className="fw-semibold">
                              {isReceivable
                                ? (row.PartyName || `Party #${row.PartyID}`)
                                : (row.VendorName || `Vendor #${row.VendorID}`)}
                            </span>
                          </td>
                          <td className="sf-col-center">
                            <span>
                              {row.vehicleTypeName ||
                                row.VehicleTypeName ||
                                vehicleTypes.find((v) => String(v.ID) === String(row.Vtype || row.VtypeId))?.VTYPE ||
                                (row.Vtype ? `Type ${row.Vtype}` : "—")}
                            </span>
                          </td>
                          {baseFields.map((f, idx) => (
                            <td
                              key={f.key}
                              className={`sf-td-base ${
                                idx === 0 ? "sf-td-base-first" : idx === baseFields.length - 1 ? "sf-td-base-last" : ""
                              } sf-col-center`}
                            >
                              <input
                                type="number"
                                step="any"
                                className="af-number-input"
                                value={row[f.key] ?? 0}
                                onChange={(e) => handleCustomRateChange(i, f.key, e.target.value)}
                                required
                              />
                            </td>
                          ))}
                          {extraFields.map((f, idx) => (
                            <td
                              key={f.key}
                              className={`sf-td-extra ${
                                idx === 0 ? "sf-td-extra-first" : idx === extraFields.length - 1 ? "sf-td-extra-last" : ""
                              } sf-col-center`}
                            >
                              <input
                                type="number"
                                step="any"
                                className="af-number-input"
                                value={row[f.key] ?? 0}
                                onChange={(e) => handleCustomRateChange(i, f.key, e.target.value)}
                                required
                              />
                            </td>
                          ))}
                          <td className="text-center">
                            <button
                              type="button"
                              className="ledger_btn_delete"
                              onClick={() => removeCustomRateRow(i)}
                              title="Remove custom rate"
                            >
                              <i className="fa-solid fa-trash-can"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          {/* Sticky Form Footer */}
          <div className="af-footer">
            <button
              type="button"
              className="filter-btn-reset"
              onClick={() => navigate(cancelUrl, { state: { isReceivable } })}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="new_ledger_btn sf-primary-btn"
              style={{ height: "30px", padding: "4px 20px" }}
              disabled={fareRows.length === 0 || saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                  Saving…
                </>
              ) : (
                <>
                  <i className="fa-solid fa-floppy-disk me-1"></i> Save Package
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Fares;